<script lang="ts" setup>
/**
 * @name Dashboard.vue
 * @version 1.0.0
 * @description Vista para mostrar datos de bitácora de instituciones
 * @author
 * @changelog
 * 1.0.0 - 2024/01/01 - @Jorel254 - Versión inicial
 */

import { computed, onMounted, ref, watch } from 'vue'
import BaseModal from '@/components/BaseModal.vue'
import { IResponsable } from '@shared/types/base'
import { useStore } from 'vuex'
import { apiService } from '@/services/apiService'
import { getInstitutionCounter } from '@/components/utils/functions'
import ResponsablesModal from '../ResponsablesModal.vue'
/**
 * @description Props del componente
 */

const error = ref<string | null>(null)
const submitting = ref(false)
const successMessage = ref<string | null>(null)
const showErrorModal = ref(false)
const showSuccessModal = ref(false)
const store = useStore()
const currentInstitutionId = ref<number>(0)
const showModalUploadCertificate = ref(false)
let responsables = ref<{ [key: string]: IResponsable }>({})

onMounted(async () => {
	currentInstitutionId.value = await getInstitutionCounter()
})
watch(
	() => store.state.global.selectedInstitution,
	async () => {
		responsables.value = JSON.parse(JSON.stringify(store.state.global.selectedInstitution.Responsable || {})) as { [key: string]: IResponsable }
	},
	{ immediate: true, deep: true }
)

const selectedInstitution = computed(() => store.state.global.selectedInstitution)

const responsablesRows = computed(
	() =>
		Object.entries(responsables.value || {})
			.map(([curp, data]) => {
				if (!curp || !data) return null

				const nombreCompleto = [data?.nombre, data?.primerApellido, data?.segundoApellido].filter(Boolean).join(' ')

				return {
					curp,
					nombreCompleto,
					phone: data?.phone ?? '',
					cargoCertificados: data?.certificates?.cargo ?? '',
					cargoTitulos: data?.degrees?.cargo ?? '',
					abrTitulo: data?.degrees?.abrTitulo ?? ''
				}
			})
			.filter(Boolean) as {
			curp: string
			nombreCompleto: string
			phone: string
			cargoCertificados: string
			cargoTitulos: string
			abrTitulo: string
		}[]
)
const uploadCertificate = () => {
	showModalUploadCertificate.value = true
}
const eliminarResponsable = (curp: string) => {
	delete responsables.value[curp]
}
const updateResponsables = (value: any) => {
	responsables.value = { ...value }
}
const saveResponsables = async () => {
	try {
		submitting.value = true
		error.value = null

		const institutionId = store.getters['global/currentInstitution']
		if (!institutionId) {
			throw new Error('No hay una institución seleccionada')
		}

		const response = await apiService.post<{ code: number; message: string }>('updateResponsables', {
			institutionId: Number(institutionId),
			Responsable: responsables.value
		})

		if (response.code === -1) {
			throw new Error(response.message || 'Error al actualizar los responsables')
		}

		successMessage.value = 'Responsables actualizados exitosamente.'
		showSuccessModal.value = true
	} catch (err: any) {
		console.error('Error al actualizar los responsables:', err)
		error.value = err.message || 'Error al actualizar los responsables'
		showErrorModal.value = true
	} finally {
		submitting.value = false
	}
}
</script>

<template>
	<div class="responsables">
		<VaCard class="bitacora-card">
			<VaCardTitle class="header-gradient">
				<div class="header-content">
					<div class="header-main">
						<div class="header-icon-wrapper">
							<va-icon name="groups" class="header-icon" />
						</div>
						<div class="header-text">
							<h2 class="header-title">Responsables de firma</h2>
							<p class="header-subtitle">Administra los responsables de expedición de certificados y títulos de la institución.</p>
						</div>
						<va-button class="action-button action-button--save" :loading="submitting" :disabled="submitting" @click="saveResponsables()">
							<va-icon name="save" class="button-icon" />
							Guardar cambios
						</va-button>
					</div>
				</div>
			</VaCardTitle>

			<VaCardContent class="card-content">
				<div class="pe-toolbar">
					<div class="pe-toolbar__meta">
						<span class="pe-chip">Institución #{{ currentInstitutionId || '—' }}</span>
						<span class="pe-chip pe-chip--accent">ID: {{ selectedInstitution?.idNombreInstitucion || 'Sin dato' }}</span>
						<span class="pe-chip">{{ responsablesRows.length }} {{ responsablesRows.length === 1 ? 'responsable' : 'responsables' }}</span>
						<p class="pe-hint">Agrega, edita o elimina responsables de firma antes de guardar los cambios.</p>
					</div>
				</div>

				<section class="pe-section">
					<div class="pe-section__head">
						<va-icon name="badge" class="pe-section__icon" />
						<h3 class="pe-section__title">Lista de responsables</h3>
						<va-button preset="primary" size="small" class="pe-add-btn" @click="uploadCertificate">
							<va-icon name="add" class="button-icon" />
							Agregar responsable
						</va-button>
					</div>

					<div v-if="responsablesRows.length" class="responsables-table">
						<div class="va-table-responsive">
							<table class="va-table va-table--striped responsables-table__inner">
								<thead>
									<tr>
										<th>CURP</th>
										<th>Nombre</th>
										<th>Teléfono</th>
										<th>Cargo (Certificados)</th>
										<th>Cargo (Títulos)</th>
										<th>Nivel de estudios</th>
										<th>Acciones</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="row in responsablesRows" :key="row.curp">
										<td>{{ row.curp }}</td>
										<td>{{ row.nombreCompleto }}</td>
										<td>{{ row.phone }}</td>
										<td>{{ row.cargoCertificados }}</td>
										<td>{{ row.cargoTitulos }}</td>
										<td>{{ row.abrTitulo }}</td>
										<td>
											<VaButton color="danger" size="small" @click="eliminarResponsable(row.curp)">
												<VaIcon name="delete" class="me-2" />
												Eliminar
											</VaButton>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
					<div v-else class="responsables-empty">Aún no hay responsables agregados.</div>
				</section>
			</VaCardContent>
		</VaCard>

		<BaseModal v-model="showErrorModal" title="Error" :message="error || 'Error al guardar los datos'" :test-mode="true" size="medium">
			<template #body>
				<div class="error-message">
					<div class="error-header">
						<div class="error-icon">
							<i class="fas fa-exclamation-triangle"></i>
						</div>
						<h3 class="error-title">Error</h3>
					</div>
					<p class="error-description">{{ error || 'Error al guardar los datos' }}</p>
				</div>
			</template>
			<template #footer>
				<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
			</template>
		</BaseModal>
		<BaseModal v-model="showSuccessModal" title="" :message="successMessage || 'Datos guardados correctamente'" :test-mode="false" size="medium">
			<template #body>
				<div class="success-message">
					<div class="success-header">
						<div class="success-icon">
							<i class="fas fa-check-circle"></i>
						</div>
						<h3 class="success-title">Éxito</h3>
					</div>
					<p class="success-description">{{ successMessage || 'Datos actualizados correctamente' }}</p>
				</div>
			</template>
			<template #footer>
				<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
			</template>
		</BaseModal>
		<ResponsablesModal v-model:isMenuOpen="showModalUploadCertificate" :responsables="responsables || {}" @update:responsables="updateResponsables" />
	</div>
</template>

<style scoped>
.responsables {
	--pe-navy: #0f3569;
	--pe-navy-deep: #062342;
	--pe-accent: #24c8ff;
	--pe-accent-2: #169bff;
	--pe-border: rgba(15, 53, 105, 0.12);
	--pe-surface: #ffffff;
	--pe-canvas: #f4f7fb;
	--pe-text: #0f3569;
	--pe-text-2: rgba(15, 53, 105, 0.72);
	--pe-radius: 16px;
	--pe-radius-sm: 12px;
	--pe-shadow: 0 8px 24px rgba(15, 53, 105, 0.08);
	display: flex;
	min-height: calc(100vh - 130px);
	width: 100%;
	max-width: 100%;
	min-width: 0;
	overflow-x: hidden;
	box-sizing: border-box;
}

.bitacora-card {
	border-radius: var(--pe-radius);
	box-shadow: var(--pe-shadow);
	animation: fadeInUp 0.5s ease-out;
	overflow: hidden;
	background: var(--pe-surface);
	border: 1px solid var(--pe-border);
	width: 100%;
	max-width: 100%;
	min-width: 0;
	display: flex;
	flex-direction: column;
}

.header-gradient {
	display: block;
	width: 100%;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	padding: 1.25rem 1.5rem;
	border-radius: 0;
	position: relative;
	text-transform: none;
	letter-spacing: normal;
	font-weight: 400;
}

.header-gradient::before {
	content: '';
	position: absolute;
	inset: 0;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	pointer-events: none;
}

.header-content {
	position: relative;
	z-index: 1;
	width: 100%;
}

.header-main {
	display: flex;
	align-items: center;
	gap: 1.25rem;
	flex-wrap: wrap;
	width: 100%;
}

.header-icon-wrapper {
	width: 56px;
	height: 56px;
	border-radius: 14px;
	background: rgba(255, 255, 255, 0.14);
	backdrop-filter: blur(8px);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	border: 1px solid rgba(255, 255, 255, 0.18);
}

.header-icon {
	color: white;
	font-size: 1.75rem;
}

.header-text {
	flex: 1;
	min-width: 200px;
}

.header-title {
	font-size: 1.5rem;
	font-weight: 700;
	color: white;
	margin: 0;
	letter-spacing: -0.02em;
	text-transform: none;
	line-height: 1.2;
}

.header-subtitle {
	margin: 0.35rem 0 0;
	font-size: 0.85rem;
	color: rgba(255, 255, 255, 0.78);
	line-height: 1.45;
	max-width: 52ch;
	text-transform: none;
	letter-spacing: normal;
	font-weight: 400;
}

.action-button--save {
	margin-left: auto;
	min-width: 190px;
	background: linear-gradient(90deg, var(--pe-accent-2), var(--pe-accent)) !important;
	border: none !important;
	font-weight: 600;
}

.card-content {
	flex: 1;
	display: flex;
	flex-direction: column;
	padding: 1.5rem;
	background: var(--pe-canvas);
	overflow-x: hidden;
	overflow-y: auto;
	min-width: 0;
}

.pe-section {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	min-width: 0;
}

.card-content::-webkit-scrollbar {
	width: 8px;
}

.card-content::-webkit-scrollbar-track {
	background: #e8eef5;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb {
	background: #9eb3cc;
	border-radius: 4px;
}

.pe-toolbar {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 1.25rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid var(--pe-border);
}

.pe-toolbar__meta {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem;
}

.pe-chip {
	display: inline-flex;
	align-items: center;
	padding: 0.3rem 0.75rem;
	border-radius: 999px;
	font-size: 0.75rem;
	font-weight: 600;
	color: var(--pe-text);
	background: rgba(15, 53, 105, 0.06);
	border: 1px solid var(--pe-border);
}

.pe-chip--accent {
	color: var(--pe-navy-deep);
	background: rgba(36, 200, 255, 0.12);
	border-color: rgba(36, 200, 255, 0.28);
}

.pe-hint {
	flex-basis: 100%;
	margin: 0.35rem 0 0;
	font-size: 0.875rem;
	color: var(--pe-text-2);
	line-height: 1.5;
}

.pe-section__head {
	display: flex;
	align-items: center;
	gap: 0.6rem;
	margin-bottom: 1rem;
	flex-wrap: wrap;
}

.pe-section__icon {
	color: var(--pe-navy-deep);
	font-size: 1.25rem;
}

.pe-section__title {
	margin: 0;
	font-size: 1.05rem;
	font-weight: 700;
	color: var(--pe-text);
	letter-spacing: -0.01em;
	flex: 1;
}

.pe-add-btn {
	margin-left: auto;
	flex-shrink: 0;
}

.responsables-table {
	flex: 1;
	background: var(--pe-surface);
	border: 1px solid var(--pe-border);
	border-radius: var(--pe-radius-sm);
	overflow: hidden;
	max-width: 100%;
	min-width: 0;
}

.responsables-table :deep(.va-table-responsive) {
	height: 100%;
	overflow-x: auto;
	-webkit-overflow-scrolling: touch;
}

.responsables-table__inner {
	width: 100%;
	min-width: 720px;
	border-collapse: collapse;
}

.responsables-table__inner :deep(thead th) {
	position: sticky;
	top: 0;
	z-index: 1;
	background: rgba(15, 53, 105, 0.05);
	color: var(--pe-navy-deep);
	font-size: 0.72rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	padding: 0.85rem 1rem;
	text-align: left;
	border-bottom: 1px solid var(--pe-border);
	white-space: nowrap;
}

.responsables-table__inner :deep(tbody td) {
	padding: 0.85rem 1rem;
	font-size: 0.875rem;
	color: var(--pe-text);
	border-bottom: 1px solid rgba(15, 53, 105, 0.06);
	vertical-align: middle;
}

.responsables-table__inner :deep(tbody tr) {
	transition: background 0.15s ease;
}

.responsables-table__inner :deep(tbody tr:hover) {
	background: rgba(36, 200, 255, 0.05);
}

.responsables-empty {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(15, 53, 105, 0.03);
	border: 1px dashed var(--pe-border);
	border-radius: var(--pe-radius-sm);
	padding: 2rem 1rem;
	text-align: center;
	color: var(--pe-text-2);
	font-size: 0.9rem;
}

.button-icon {
	margin-right: 0.35rem;
}

.error-description,
.success-description {
	font-size: 1rem;
	line-height: 1.6;
	margin-bottom: 1rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
}

.error-description {
	border-left: 4px solid #f71d1d;
}

.success-description {
	border-left: 4px solid #28a745;
}

.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 1px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 24px rgba(254, 178, 178, 0.25);
	animation: fadeInUp 0.35s ease-out;
}

.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 1px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 24px rgba(40, 167, 69, 0.2);
	animation: fadeInUp 0.35s ease-out;
}

.error-header,
.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid rgba(255, 255, 255, 0.35);
}

.error-icon,
.success-icon {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.error-icon {
	background: #e67a7a;
	box-shadow: 0 4px 12px rgba(245, 101, 101, 0.35);
}

.success-icon {
	background: #1e7e34;
	box-shadow: 0 4px 12px rgba(40, 167, 69, 0.35);
}

.error-icon i,
.success-icon i {
	color: white;
	font-size: 1.35rem;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.35rem;
	font-weight: 700;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.35rem;
	font-weight: 700;
}

:deep(.va-tabs) {
	width: 100%;
}

:deep(.va-tabs__content) {
	width: 100%;
	display: block;
}

:deep(.va-tabs__content > .va-card) {
	width: 100% !important;
	max-width: 100% !important;
	margin-left: 0 !important;
	margin-right: 0 !important;
}

@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(16px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (max-width: 768px) {
	.card-content {
		padding: 1rem;
	}

	.header-gradient {
		padding: 1rem;
	}

	.header-main {
		flex-direction: column;
		align-items: stretch;
	}

	.action-button--save {
		margin-left: 0;
		width: 100%;
	}

	.pe-section__head {
		flex-direction: column;
		align-items: stretch;
	}

	.pe-add-btn {
		margin-left: 0;
		width: 100%;
	}

	.responsables-table__inner {
		min-width: 640px;
	}

	.responsables-table__inner :deep(thead th),
	.responsables-table__inner :deep(tbody td) {
		padding: 0.65rem 0.75rem;
		font-size: 0.8rem;
	}
}
</style>
